import React, { useState, useEffect } from 'react';
import { Bell, X, Check } from 'lucide-react';
import { Button } from './ui/button';
import usePushNotifications from '../hooks/usePushNotifications';

const PushNotificationPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const { permission, isSupported, requestPermission, isGranted, isDenied } = usePushNotifications();

  useEffect(() => {
    // Check if user has already dismissed the prompt or already granted permission
    const hasBeenDismissed = localStorage.getItem('pushNotificationsDismissed');
    
    if (isSupported && !hasBeenDismissed && permission === 'default') {
      // Show prompt after a short delay (2 seconds) to not interrupt initial page load
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isSupported, permission]);

  const handleAccept = async () => {
    const granted = await requestPermission();
    setShowPrompt(false);
    
    if (granted) {
      // Store preference
      localStorage.setItem('pushNotificationsEnabled', 'true');
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    localStorage.setItem('pushNotificationsDismissed', 'true');
  };

  const handleRemindLater = () => {
    setShowPrompt(false);
    // Will show again on next session
  };

  // Don't show if not supported, already granted, or denied
  if (!isSupported || isGranted || isDenied || !showPrompt || dismissed) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-right-2 duration-300">
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 max-w-sm">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Bell className="w-5 h-5 text-blue-600" />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 text-sm">
              Stay Updated
            </h3>
            <p className="text-gray-600 text-xs mt-1">
              Get notified about new messages, reservation requests, and important updates.
            </p>
            
            <div className="flex items-center gap-2 mt-3">
              <Button
                onClick={handleAccept}
                size="sm"
                className="btn-snow text-xs h-8"
              >
                <Check className="w-3 h-3 mr-1" />
                Enable
              </Button>
              <Button
                onClick={handleRemindLater}
                variant="outline"
                size="sm"
                className="text-xs h-8"
              >
                Later
              </Button>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PushNotificationPrompt;
